import 'core-js/stable';
import '@babel/polyfill';
import Vue, { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import portalapi from '@services/portalapi';
import CoreuiVue from '@coreui/vue-pro';
import { iconsSet as icons } from './assets/icons/icons.js';
import { CIcon } from '@coreui/icons-vue';
import store from './store/store';
import ToastPlugin from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import ModalLoginHelp from './views/pages/ModalLoginHelp.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { 
    faPrescriptionBottleMedical, 
    faFileSignature, 
    faCaretDown,
    faCaretUp,
    faStar,
    faKey,
    faAngleLeft,
    faAngleRight,
    faListCheck,
    faClipboardCheck,
} from '@fortawesome/free-solid-svg-icons';
import { 
    faCircleQuestion,
    faEye,
    faEyeSlash,
    faIdBadge,
    faHourglassHalf,
} from '@fortawesome/free-regular-svg-icons';
import exportFromJSON from 'export-from-json';
import SimpleTypeahead from 'vue3-simple-typeahead';
import 'vue3-simple-typeahead/dist/vue3-simple-typeahead.css';

// Going outside the portal project folder to get the shared BrowserCheck module
import BrowserCheck from '@services/browserCheck';

// Import constants from the Constants.js file
import Constants from "./constants/Constants";
import VueGtag from "vue-gtag";

/* add icons to Font Awesome library */
library.add(faPrescriptionBottleMedical);
library.add(faFileSignature);
library.add(faCaretDown);
library.add(faCaretUp);
library.add(faAngleLeft);
library.add(faAngleRight);
library.add(faStar);
library.add(faKey);
library.add(faListCheck);
library.add(faClipboardCheck);

library.add(faCircleQuestion);
library.add(faEye);
library.add(faEyeSlash);
library.add(faIdBadge);
library.add(faHourglassHalf);

window.vm = createApp(App);
window.vm.use(store);
window.vm.use(router);
window.vm.use(CoreuiVue);
window.vm.use(ToastPlugin, {
    position: 'top-right',
    duration: parseInt(process.env.VUE_APP_TOAST_DURATION) ?? 4000,
});
window.vm.use(SimpleTypeahead);
window.vm.use(
    VueGtag, {
        config: {
            id: process.env.VUE_APP_GTAG_ID
        }
    },
    router
);
window.vm.provide('icons', icons);
window.vm.component('CIcon', CIcon);
window.vm.component('font-awesome-icon', FontAwesomeIcon);
window.vm.component('ModalLoginHelp', ModalLoginHelp);

// Use Constants file
window.vm.config.globalProperties.$constants = Constants;

// Set Page Title
document.title = window.vm.config.globalProperties.$constants.PAGE_TITLE;

// Add global filters
window.vm.config.globalProperties.$filters = {
    /**
     * Format a value to currency with dollar separators and dollar sign
     */
    currency(value, displayNullAsZero = true, decimals = 2) {
        // Check if value is false or contains letters
        // This is specifically for the GST/HST and PST fields on the Order History page
        if (!value || /[a-zA-Z]/.test(value)) {
            if (displayNullAsZero) {
                return '$0.00';
            }
            return null;
        }

        // If the value is negative, store the sign and remove it from the value
        // so we can prepend it to the formatted value later
        let sign = '';
        if (value < 0) {
            sign = '-';
            value *= -1;
        }

        value = Number.parseFloat(value).toFixed(decimals);

        // Work only with the dollar portion of the value
        let decimalPos = value.indexOf('.');
        let dollars = decimalPos != -1 ? value.substring(0, decimalPos) : value;

        // Format the dollar portion of the value by starting at the end and
        // separating groups of three digits with commas
        let formatted = '';
        for (let i = dollars.length - 3; i > 0; i -= 3) {
            let curPortion = dollars.substring(i);
            if (curPortion.length === 3) {
                formatted =
                    curPortion + (formatted != '' ? ',' : '') + formatted;
                dollars = dollars.substring(0, i);
            }
        }

        // Add the last - or only - portion of the value
        if (dollars != '') {
            formatted = dollars + (formatted != '' ? ',' : '') + formatted;
        }

        // Append the decimal portion if there is one
        if (decimalPos != -1) {
            formatted = formatted + value.substring(decimalPos);
        }

        return sign + '$' + formatted;
    },
    /** 
     * Format a value as a float
     */
    float(value, keepNulls = false) {

        if (!value) {
            return (keepNulls ? null : '0.00');
        }

        var result = Number.parseFloat(value).toFixed(2);

        if (result == 0) return '0.00';

        return result;
    },
    /**
     * Format integer values to a string with commas
     */
    integer(value, keepNulls = false) {
        if (!value) {
            return (keepNulls ? null : '0');
        }

        // Convert to integer and format with commas
        value = Number.parseInt(value).toLocaleString();

        return value;

    },
    /**
     * Format a value to weight
     */
    weight(value) {
        if (!value) return '0.000';

        value = Number.parseFloat(value).toFixed(3);

        return value + ' lbs';
    },
    /**
     * Format a value to volume
     */
    volume(value) {
        if (!value) return '0.000';

        value = Number.parseFloat(value).toFixed(3);

        return value + ' ft3';
    },
    /**
     * Format a value to length
     */
    length(value) {
        if (!value) return '0.000';

        value = Number.parseFloat(value).toFixed(3);

        return value + ' in';
    },
    /**
     * Formats a date into MM/DD/YYYY string format
     * @param {string|Date} dateInput - Date as string or Date object
     * @param {boolean} [displayNullDate=true] - If true, return formatted date for DB null dates (01/01/1900) 
     * @returns {string} Formatted date string in MM/DD/YYYY format
     */
    date(dateInput, displayNullDate = true) {
        // If input is null or undefined, return empty string
        if (dateInput === null || dateInput === undefined || dateInput === '') {
            return '';
        }

        let dateObj;

        // Check if input is already a Date object
        if (dateInput instanceof Date) {
            dateObj = dateInput;
        } else if (typeof dateInput === 'string') {
            // Handle string input
            const originalInput = dateInput;

            // Remove any time component if present
            const datePart = dateInput.split(' ')[0];

            // Check for different separators
            let parts;
            let year, month, day;

            if (datePart.includes('-')) {
                parts = datePart.split('-');
            } else if (datePart.includes('/')) {
                parts = datePart.split('/');
            } else {
                // Try standard Date parsing as fallback
                dateObj = new Date(dateInput);
                if (!isNaN(dateObj.getTime())) {
                    // Use UTC methods to avoid timezone issues
                    year = dateObj.getUTCFullYear();
                    month = dateObj.getUTCMonth() + 1;
                    day = dateObj.getUTCDate();
                    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
                }
                return 'Invalid date format';
            }

            // Determine if format is MM-DD-YYYY or YYYY-MM-DD
            if (parts[0].length === 4) {
                // YYYY-MM-DD format
                year = parseInt(parts[0]);
                month = parseInt(parts[1]);
                day = parseInt(parts[2]);
            } else {
                // MM-DD-YYYY format
                month = parseInt(parts[0]);
                day = parseInt(parts[1]);
                year = parseInt(parts[2]);
            }

            // Create date using UTC to avoid timezone issues
            // Note: months are 0-indexed in JavaScript Date
            var formattedDate = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;

            if (!displayNullDate && formattedDate === '01/01/1900') {
                return null;
            }

            return formattedDate;
        } else {
            return 'Invalid input type';
        }

        // If we have a Date object at this point, format it
        if (dateObj && !isNaN(dateObj.getTime())) {
            // Use UTC methods to avoid timezone issues
            const year = dateObj.getUTCFullYear();
            const month = dateObj.getUTCMonth() + 1;
            const day = dateObj.getUTCDate();
            return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
        }

        return 'Invalid date';
    },
    /**
     * Formats a time into HH:MM:SS or HH:MM AM/PM string format
     * @param {string|Date} timeInput - Time as string, Date object, or time string (HH:MM:SS)
     * @param {boolean} [use24Hour=false] - If true, return 24-hour format; if false, return 12-hour format with AM/PM
     * @param {boolean} [includeSeconds=false] - If true, include seconds in output
     * @returns {string} Formatted time string
     */
    time(timeInput, use24Hour = true, includeSeconds = false) {
        // If input is null or undefined, return empty string
        if (timeInput === null || timeInput === undefined || timeInput === '') {
            return '';
        }

        let hours, minutes, seconds;

        // Check if input is a Date object
        if (timeInput instanceof Date) {
            hours = timeInput.getHours();
            minutes = timeInput.getMinutes();
            seconds = timeInput.getSeconds();
        } else if (typeof timeInput === 'string') {
            // Handle time string formats like "HH:MM:SS", "HH:MM", or "HHMM"
            let timePart = timeInput;

            // If the string contains a date part, extract just the time
            if (timeInput.includes(' ')) {
                const parts = timeInput.split(' ');
                // Check if there's a time component after the date
                if (parts.length > 1 && parts[1].includes(':')) {
                    timePart = parts[1];
                }
            }

            // Parse time string with colons
            if (timePart.includes(':')) {
                const timeParts = timePart.split(':');
                hours = parseInt(timeParts[0]);
                minutes = parseInt(timeParts[1]);
                seconds = timeParts.length > 2 ? parseInt(timeParts[2]) : 0;
            } 
            // Parse time string without colons (HHMM or HHMMSS format)
            else if (timePart.length === 4 || timePart.length === 6) {
                hours = parseInt(timePart.substring(0, 2));
                minutes = parseInt(timePart.substring(2, 4));
                seconds = timePart.length === 6 ? parseInt(timePart.substring(4, 6)) : 0;
            } else {
                return 'Invalid time format';
            }

            // Validate parsed values
            if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
                return 'Invalid time format';
            }
        } else {
            return 'Invalid input type';
        }

        // Validate time values
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
            return 'Invalid time values';
        }

        // Format based on 24-hour or 12-hour preference
        if (use24Hour) {
            // 24-hour format
            const formattedHours = hours.toString().padStart(2, '0');
            const formattedMinutes = minutes.toString().padStart(2, '0');
            const formattedSeconds = seconds.toString().padStart(2, '0');

            if (includeSeconds) {
                return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
            }
            return `${formattedHours}:${formattedMinutes}`;
        } else {
            // 12-hour format with AM/PM
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            const formattedMinutes = minutes.toString().padStart(2, '0');
            const formattedSeconds = seconds.toString().padStart(2, '0');

            if (includeSeconds) {
                return `${displayHours}:${formattedMinutes}:${formattedSeconds} ${period}`;
            }
            return `${displayHours}:${formattedMinutes} ${period}`;
        }
    },
    /**
     * Checks if a date is within a given range 
     * @param {string|Date} targetDate - Date as string or Date object
     * @param {string|Date} startDate - Start of date range
     * @param {string|Date} endDate - End of date range
     * @returns {boolean} True if target date is within range
     */ 
    isDateWithinRange(targetDate, startDate, endDate) {
        const target = new Date(this.date(targetDate));
        const start = new Date(this.date(startDate)).setHours(0, 0, 0, 0);
        const end = new Date(this.date(endDate)).setHours(23, 59, 59, 999);
        
        if (targetDate == null) {
            return false;
        }

        if (startDate != null && target < start) {
            return false;
        }

        if (endDate != null && target > end) {
            return false;
        }

        return true;
    },
};

/*
 * Shared function to navigate to the item details page with optional tab selection
 */
window.vm.config.globalProperties.goToItemPage = function(itemNumber, tab = null) {
    let params = { itemNumber: itemNumber };

    if (tab) {
        params.displayTab = tab;
    }

    this.$router.push({
        name: 'Item Details',
        params: params,
    });
};

/*
 * TODO: Not yet functional. Remove once all actions are functional.
 */
window.vm.config.globalProperties.nonFunctional = function(e) {
    if (e) e.preventDefault();
    
    vm.config.globalProperties.$toast.open({
        message: 'This is not yet functional',
        type: 'info',
    });
    
    return false;
};

/*
 * Export JSON data to CSV
 */
window.vm.config.globalProperties.exportToCsv = function(data, fileName) {
    const exportType = exportFromJSON.types.csv;
    
    exportFromJSON({
        data,
        fileName,
        exportType,
    });
};

// Add function to update standard router parameters for search pages
window.vm.config.globalProperties.updateRouterParameters = function(page, itemsPerPage, sortBy, filterValues) {
    const query = { ...this.$route.query };

    // Update Active Page
    query.page = page;

    // Update Items Per Page
    query.itemsPerPage = itemsPerPage;

    // Update Sort By
    query.sortBy = JSON.stringify(sortBy);

    // Update Filter Values
    query.filters = JSON.stringify(filterValues);

    this.$router.replace({ query }).catch(() => {});
};

// Shared functions to handle pagination
window.vm.config.globalProperties.$pagination = {

    // Handles updating the sort column
    handleSortChange(val, dataFetchMethod) {
        this.sortBy = val;
        this.activePage = 1;

        // Call the provided data fetching method
        dataFetchMethod.call(this);
    },
    
    // Handles updating the active page
    handleActivePageChange(val, dataFetchMethod) {
        if (this.activePage === val) {
            return;
        }

        this.activePage = val;

        // Call the provided data fetching method
        dataFetchMethod.call(this);
    },

    // Handles updating the items per page
    handleItemsPerPageChange(val, dataFetchMethod, useDefaultPageSize = true) {
        if (this.itemsPerPage === val) {
            return;
        }

        if (useDefaultPageSize) {
            // Update the user's default page size preference
            const payload = {
                userId: this.$store.state.User.userData.userId,
                storeId: this.$store.state.User.userData.storeId,
                defaultPageSize: val,
            };

            this.$store.dispatch('User/updatePageSizePreference', payload);
        }

        this.itemsPerPage = val;
        this.activePage = 1;

        // Call the provided data fetching method
        dataFetchMethod.call(this);
    },

    // Initialize component state from URL parameters
    initializeFromUrlParams(filteringMethod, ...filteringArgs) {
        // Initialize URL query parameters
        const query = this.$route.query;

        // Active Page
        if (query.page) {
            this.activePage = parseInt(query.page);
        }

        // Items Per Page
        if (query.itemsPerPage) {
            this.itemsPerPage = parseInt(query.itemsPerPage);
        } else if (this.$store.state.User.userData.defaultPageSize) {
            this.itemsPerPage = parseInt(this.$store.state.User.userData.defaultPageSize);
        }

        // Sort By
        if (query.sortBy) {
            try {
                this.sortBy = JSON.parse(query.sortBy);
            } catch (e) {
                console.error('Error parsing sortBy from URL query:', e);
            }
        }

        // Filter Values
        if (query.filters) {
            try {
                this.filterValues = JSON.parse(query.filters);

                // Call the provided filtering method with any additional arguments
                filteringMethod.call(this, ...filteringArgs);
            } catch (e) {
                console.error('Error parsing filterValues from URL query:', e);
            }
        }
    },
};

/**
 * Fetch resource types from the API and seed the Vuex store before mounting.
 * Resource type routes are now handled by the single parameterized route
 * /helpAndResources/:resourceType in router/index.js — no dynamic route
 * registration is needed here.
 *
 * If the API call fails, the app still mounts — resource pages will fall back
 * to fetching types on demand via HelpAndResources.vue's mounted() hook.
 */
(async () => {
    try {
        const response = await portalapi.moduleResources.getResourceTypes();
        if (response && response.status === 200 && Array.isArray(response.data)) {
            // Seed the Vuex store so components can look up descriptions,
            // display options, nav links, etc. without a separate API call.
            store.commit('Resources/setResourceTypes', response.data);
        }
    } catch (e) {
        console.error('[main.js] Failed to load resource types:', e);
    }

    window.vm.mount('#app');
})();

// Display an alert message if the user is using IE
let browserType = BrowserCheck.getBrowserType();
if (browserType == 'IE') {
    let warningHtml =
        '<div class="alert alert-warning">Sorry, Internet Explorer is no longer supported. Please install a more up to date browser such as Edge, Chrome, Firefox, or Opera.</div>';
    document.body.innerHTML = warningHtml + document.body.innerHTML;
}
